import _ from 'lodash';
import {createSelector} from 'reselect';

import {
  hasFeature,
  isIntegrationUncounted,
  maxNumberOfFeatureType,
} from '../../../helpers/models/billingHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {mapCrmSyncSourceToIntegrationType} from '../../../models/api/crmSyncModel';
import {IntegrationSettingsTypesEnum} from '../../../models/api/integrationModel';
import {getCompany} from '../company/companySelectors';
import {getCrmSyncSource} from '../crmSync/crmSyncSelectors';
import {getEnabledIntegrationsIds, getFlatIntegrations} from '../dataSelectors';
import {makeGetIsIntegrationTypeEnabled} from '../integrationTypes/integrationTypesSelectors';

/*
 * Selectors.
 */

export const getCrmIntegration = createSelector(
  [getFlatIntegrations, getCrmSyncSource],
  (integrations, source) => {
    const type = mapCrmSyncSourceToIntegrationType(source);
    return _(integrations)
      .filter((integration) => integration?.type === type)
      .first();
  },
);

export const getSalesforceIntegration = createSelector(getFlatIntegrations, (integrations) => {
  for (const integration of integrations) {
    if (integration.customSettingsType === IntegrationSettingsTypesEnum.SALESFORCE) {
      return integration;
    }
  }
  return undefined;
});

export const getHubspotIntegration = createSelector(getFlatIntegrations, (integrations) => {
  for (const integration of integrations) {
    if (integration.customSettingsType === IntegrationSettingsTypesEnum.HUBSPOT) {
      return integration;
    }
  }
  return undefined;
});

export const getGrammarlyIntegration = createSelector(getFlatIntegrations, (integrations) => {
  for (const integration of integrations) {
    if (integration.customSettingsType === IntegrationSettingsTypesEnum.GRAMMARLY) {
      return integration;
    }
  }
  return undefined;
});

export const getIsGrammarlyIntegrationEnabled = createSelector(getEnabledIntegrationsIds, (integrationIds) =>
  _(integrationIds).includes(IntegrationSettingsTypesEnum.GRAMMARLY),
);

export function makeGetIsIntegrationUncounted(
  getIntegrationId: AppParametricSelector<any, string | undefined>,
) {
  return createSelector([getCompany, getIntegrationId], (company, integrationId) => {
    if (!integrationId) {
      return false;
    }
    return isIntegrationUncounted(company.features, convertIntegrationIdToBillingId(integrationId));
  });
}

export const getMaxNumIntegrationsEnabled = createSelector([getCompany], (company) =>
  maxNumberOfFeatureType('integrations', company.features, 1),
);

export const getNumEnabledIntegrations = createSelector(
  [getCompany, getFlatIntegrations],
  (company, integrations) =>
    _(integrations)
      .filter(
        (integration) =>
          integration.isEnabled &&
          !isIntegrationUncounted(company.features, convertIntegrationIdToBillingId(integration.type)),
      )
      .map((integration) => integration.type)
      .uniq()
      .value().length,
);

export const getIsAtMaxEnabledIntegrationLimit = createSelector(
  [getMaxNumIntegrationsEnabled, getNumEnabledIntegrations],
  (maxNumIntegrations, numIntegrations) => numIntegrations >= maxNumIntegrations,
);

export function makeGetIsIntegrationAvailableOnPlan(
  getIntegrationId: AppParametricSelector<any, string | undefined>,
) {
  return createSelector([getCompany, getIntegrationId], (company, integrationId) => {
    if (!integrationId) {
      return false;
    }

    const convertedIntegrationId = convertIntegrationIdToBillingId(integrationId);
    // Hard-coded like the backend. https://github.com/frontapp/front/blob/master/server/pricing/features.ts#L46
    if (convertedIntegrationId === 'default') {
      return true;
    }
    return (
      hasFeature(`channel:${convertedIntegrationId}`, company.features) ||
      hasFeature(`int:${convertedIntegrationId}`, company.features) ||
      hasFeature(`int0:${convertedIntegrationId}`, company.features)
    );
  });
}

export function makeGetIsIntegrationDisabledViaBilling(
  getIntegrationId: AppParametricSelector<any, string | undefined>,
) {
  return createSelector(
    [
      makeGetIsIntegrationTypeEnabled(getIntegrationId),
      makeGetIsIntegrationAvailableOnPlan(getIntegrationId),
      makeGetIsIntegrationUncounted(getIntegrationId),
      getIsAtMaxEnabledIntegrationLimit,
    ],
    (
      isIntegrationEnabled,
      isIntegrationAvailableOnPlan,
      isIntegrationUncountedForCompany,
      isAtMaxEnabledLimit,
    ) => {
      // If the integration is enabled already we can ignore the check.
      if (isIntegrationEnabled) {
        return false;
      }
      // If the integration is uncounted, we can always enable it.
      if (isIntegrationUncountedForCompany) {
        return false;
      }
      // The integration is not available on their plan.
      if (!isIntegrationAvailableOnPlan) {
        return true;
      }
      // They are at their limit for integrations available on their plan.
      if (isAtMaxEnabledLimit) {
        return true;
      }
      return false;
    },
  );
}

/*
 * Helpers.
 */

/**
 * The billing for some channels/integrations are sometimes different than the id,
 * this converts them to the correct id to check for for.
 */
function convertIntegrationIdToBillingId(integrationTypeId: string) {
  switch (integrationTypeId) {
    case 'googleplay':
      return 'google_play';
    case 'whatsapp':
      return 'twilio_whatsapp';
    default:
      return integrationTypeId;
  }
}
